import { cloneDeep } from "lodash";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AcceptableFileType } from "../../utils/acceptable-file-types";
import { Nullable } from "../types/Nullable";
import { useHtmlId } from "../utils/useHtmlId";
import { showToast } from "@/flokit/core/toast/show-toast";

export interface FileUploadPayload {
  file: Nullable<File>;
  fileName: Nullable<string>;
  fileType: Nullable<string>;
  fileSize: Nullable<number>;
}

type FileChangeHandler = (args: FileUploadPayload) => void;

const EMPTY_PAYLOAD: FileUploadPayload = {
  file: null,
  fileName: null,
  fileType: null,
  fileSize: null,
};

// Error handling
// #region

type FileError = {
  isInvalid: boolean;
  message: string;
};

const fileErrorTypes = ["fileSize", "fileType"] as const;
type FileErrorType = (typeof fileErrorTypes)[number];
type _FileErrors = { [_ in FileErrorType]: FileError };

export interface FileErrorState extends _FileErrors {
  /**
   * Convenience field for checking whether any errors occurred while uploading.
   *
   * One of the error messages to display, if any. Otherwise, `null`.
   */
  hasError: boolean;
}

const defaultFileErrors: FileErrorState = {
  fileSize: {
    isInvalid: false,
    message: "File too large.",
  },
  fileType: {
    isInvalid: false,
    message: "Incorrect file type.",
  },
  hasError: false,
};

type FileErrorMessages = {
  [_ in FileErrorType]?: string;
};

function overwriteDefaultErrorMessages(
  messages?: FileErrorMessages,
): FileErrorState {
  if (!messages) return defaultFileErrors;

  const result = cloneDeep(defaultFileErrors);

  for (let [key, value] of Object.entries(messages)) {
    if (fileErrorTypes.includes(key as FileErrorType)) {
      result[key].message = value;
    }
  }

  return result;
}

export type FileErrorObject = {
  errorType: FileErrorType;
  message: string;
};
function toErrorArray(errorState: FileErrorState): FileErrorObject[] {
  let result: FileErrorObject[] = [];

  Object.keys(errorState).forEach((k: keyof FileErrorState) => {
    if (fileErrorTypes.includes(k as FileErrorType)) {
      const key = k as FileErrorType;
      const val = errorState[key];

      if (val.isInvalid) {
        result.push({
          errorType: key,
          message: val.message,
        });
      }
    }
  });

  return result;
}

// #endregion

export type FileErrorCallback = (errors: FileErrorState) => void;
interface UseFileUploadProps {
  onChange: FileChangeHandler;
  maxFileSize: number;
  fileTypes: readonly AcceptableFileType[];
  errorMessages?: FileErrorMessages;
}
interface UseFileUploadReturn {
  payload: FileUploadPayload;
  /**
   * Only necessary if the `<input type="file" />` is not connected to a `<label />`, since clicking the `<label />` will ask the user for input.
   */
  askUserForFile: () => void;
  resetFileState: () => void;
  /**
   * Object describing error state.
   */
  errorState: FileErrorState;
  /**
   * List of errors.
   */
  errorArray: FileErrorObject[];
  /**
   * `id` attribute of the underlying `<input type="file" />` element.
   */
  inputId: string;
}
export function useFileUpload(props: UseFileUploadProps): UseFileUploadReturn {
  const inputId = useHtmlId();

  const ref = useRef<Nullable<HTMLInputElement>>(null);

  const [payload, setPayload] = useState<FileUploadPayload>(EMPTY_PAYLOAD);

  const onChangeHandler = useRef<Nullable<FileChangeHandler>>();
  onChangeHandler.current = props.onChange ?? null;

  const [errorState, setErrorState] = useState<FileErrorState>(
    overwriteDefaultErrorMessages(props?.errorMessages),
  );

  // Helpers for error handling
  // #region

  const setInvalidity = useCallback(
    (errorType: FileErrorType, isInvalid: boolean) =>
      setErrorState((e) => {
        const newErrorState = {
          ...e[errorType],
          isInvalid,
        };

        // If being marked invalid, then certainly have an error
        if (isInvalid)
          return {
            ...e,
            [errorType]: newErrorState,
            hasError: true,
          };

        // Otherwise, check if there are any other errors
        for (let errorType of fileErrorTypes) {
          const errorState = e[errorType];

          if (errorState.isInvalid) {
            return {
              ...e,
              [errorType]: newErrorState,
              hasError: true,
            };
          }
        }

        return {
          ...e,
          [errorType]: newErrorState,
          hasError: false,
        };
      }),
    [],
  );

  const setValid = useCallback(
    (errorType: FileErrorType) => setInvalidity(errorType, false),
    [setInvalidity],
  );

  const resetErrors = useCallback(() => {
    // Calling setValid on all keys will set hasError to "false" at the end.
    for (let key of fileErrorTypes) {
      setValid(key);
    }
  }, [setValid]);

  const setInvalid = useCallback(
    (errorType: FileErrorType) => setInvalidity(errorType, true),
    [setInvalidity],
  );

  const errorArray = useMemo(() => {
    return toErrorArray(errorState);
  }, [errorState]);

  // #endregion

  useLayoutEffect(() => {
    // 'browser' doesn't exist on the  'NodeJS.Process' type
    // https://stackoverflow.com/questions/65416642/process-browser-does-not-exist
    // @ts-ignore
    if (process.browser && !ref.current) {
      // create input on document
      const input = document.createElement("input");
      ref.current = input;

      input.type = "file";

      input.id = inputId;

      // accept the file types in the opts.fileTypes array
      if (props.fileTypes) {
        input.accept = props.fileTypes.join(",");
      }

      // Input must be in DOM for Selenium to interact with it
      document.body.appendChild(input);

      // Hide from sight
      document.body.style.position = "relative";
      input.style.width = "0";
      input.style.position = "absolute";
      // Hide from screen reader
      input.tabIndex = -1;

      input.onchange = () => {
        const file = (input.files ?? [])[0];

        let hasErrors = false;

        if (!file) {
          return;
        }

        // Check file type
        if (!props.fileTypes.includes(file.type as AcceptableFileType)) {
          setInvalid("fileType");
          hasErrors = true;
          showToast({
            message: props.errorMessages?.fileType ?? "",
            type: "error",
          });
        } else setValid("fileType");

        // Check file size
        if (file.size > props.maxFileSize) {
          setInvalid("fileSize");
          hasErrors = true;
          showToast({
            message: props.errorMessages?.fileSize ?? "",
            type: "error",
          });
        } else setValid("fileSize");

        if (hasErrors) return;

        const payload = {
          file,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        };
        setPayload(payload);

        onChangeHandler.current?.(payload);
      };
    }
    return () => {
      // remove input on unmount
      if (ref.current) {
        ref.current.value = null;
        ref.current.remove();
        ref.current = null;
      }
    };
  }, [props, setValid, setInvalid, inputId]);

  const resetFileState = useCallback(() => {
    if (ref.current) {
      ref.current.value = null;
      setPayload(EMPTY_PAYLOAD);
      resetErrors();
    }
  }, [resetErrors]);

  const askUserForFile = useCallback(() => {
    if (ref.current) {
      ref.current.click();
    } else {
      throw new Error("No input element reference found");
    }
  }, []);

  return {
    payload,
    askUserForFile,
    resetFileState,
    errorState,
    errorArray,
    inputId,
  };
}

export interface UseIframeSafeDragAndDropFileProps extends UseFileUploadProps {
  onError: FileErrorCallback;
}

export const useIframeSafeDragAndDropFile = (
  props: UseIframeSafeDragAndDropFileProps,
) => {
  const dropTarget = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const [payload, setPayload] = useState<FileUploadPayload>(EMPTY_PAYLOAD);

  const [errorState, setErrorState] = useState<FileErrorState>({
    fileType: {
      isInvalid: false,
      message: props?.errorMessages?.fileType ?? "File type is not allowed",
    },
    fileSize: {
      isInvalid: false,
      message: props?.errorMessages?.fileSize ?? "File size is too large",
    },
    hasError: false,
  });

  const onChangeHandler = useRef<Nullable<FileChangeHandler>>();
  onChangeHandler.current = props.onChange ?? null;

  // We don't want to remove the event listeners when this changes
  // and asking the user to memoize this seems excessive.
  const fileTypesRef = useRef(props.fileTypes);
  fileTypesRef.current = props.fileTypes;

  const errorCallback = useRef<Nullable<FileErrorCallback>>(props.onError);
  errorCallback.current = props.onError;

  // had to go with this approach instead of react-dropzone to handle
  // the drag and drop in an iframe.
  useEffect(() => {
    const elem = dropTarget.current;
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e) => {
      const file = e.dataTransfer.files[0];
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const errors: {
        [_ in FileErrorType]: boolean;
      } = {
        fileSize: false,
        fileType: false,
      };

      if (!file) {
        return;
      }

      if (file.size > props.maxFileSize) {
        errors.fileSize = true;
        showToast({
          message: props.errorMessages?.fileSize ?? "",
          type: "error",
        });
      }
      if (!fileTypesRef.current.includes(file.type)) {
        errors.fileType = true;
        showToast({
          message: props.errorMessages?.fileType ?? "",
          type: "error",
        });
      }

      if (Object.values(errors).some((e) => e)) {
        setErrorState((err) => {
          return {
            ...err,
            fileSize: {
              ...err.fileSize,
              isInvalid: errors.fileSize,
            },
            fileType: {
              ...err.fileType,
              isInvalid: errors.fileType,
            },
            hasError: true,
          };
        });
        return;
      }

      setPayload({
        file,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });

      onChangeHandler.current?.({
        file,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });
    };

    elem?.addEventListener?.("dragover", handleDragOver);
    elem?.addEventListener?.("drop", handleDrop);
    elem?.addEventListener?.("dragleave", handleDragLeave);

    return () => {
      elem?.removeEventListener?.("dragover", handleDragOver);
      elem?.removeEventListener?.("drop", handleDrop);
      elem?.removeEventListener?.("dragleave", handleDragLeave);
    };
  }, [setPayload, props.maxFileSize, props.errorMessages]);

  useEffect(() => {
    // Doing it this way so we can use the latest error state
    // otherwise we'd need to have sideffects in the setErrorState call and that's gross
    if (errorState.hasError) {
      errorCallback.current?.(errorState);
    }
  }, [errorState]);

  return {
    payload,
    dropTarget,
    isDragging,
    errorState,
  };
};
